import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Trash2, Plus, Edit } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

const EnhancedPackageManager = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedPackageId, setSelectedPackageId] = useState<number | null>(null);

  // Fetch packages
  const { data: packages = [] } = useQuery({
    queryKey: ["packages-enhanced"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sns_packages")
        .select("*")
        .order("id");
      if (error) throw error;
      return data;
    }
  });

  // Fetch pricing for selected package
  const { data: pricing } = useQuery({
    queryKey: ["package-pricing", selectedPackageId],
    queryFn: async () => {
      if (!selectedPackageId) return null;
      const { data, error } = await supabase
        .from("sns_package_pricing")
        .select("*")
        .eq("package_id", selectedPackageId)
        .single();
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
    enabled: !!selectedPackageId
  });

  // Fetch addons for selected package
  const { data: addons = [] } = useQuery({
    queryKey: ["package-addons", selectedPackageId],
    queryFn: async () => {
      if (!selectedPackageId) return [];
      const { data, error } = await supabase
        .from("sns_package_addons")
        .select("*")
        .eq("package_id", selectedPackageId)
        .order("sort_order");
      if (error) throw error;
      return data;
    },
    enabled: !!selectedPackageId
  });

  // Fetch rooms for selected package
  const { data: rooms = [] } = useQuery({
    queryKey: ["room-packages", selectedPackageId],
    queryFn: async () => {
      if (!selectedPackageId) return [];
      const { data, error } = await supabase
        .from("sns_room_packages")
        .select("*")
        .eq("package_id", selectedPackageId);
      if (error) throw error;
      return data;
    },
    enabled: !!selectedPackageId
  });

  const [newPricing, setNewPricing] = useState({
    base_price: 0,
    per_person_price: 0,
    per_night_price: 0,
    min_nights: 1,
    max_nights: 30,
    min_persons: 1,
    max_persons: 10
  });

  const [newAddon, setNewAddon] = useState({
    name: "",
    description: "",
    price: 0,
    addon_type: "activity",
    is_per_person: false,
    is_per_night: false,
    sort_order: 0
  });

  const [newRoom, setNewRoom] = useState({
    room_type: "",
    room_name: "",
    capacity: 2,
    base_price: 0,
    price_per_extra_person: 0,
    max_extra_persons: 0,
    amenities: [] as string[]
  });

  const [editingRoom, setEditingRoom] = useState<any>(null);

  // Mutations
  const savePricing = useMutation({
    mutationFn: async () => {
      if (!selectedPackageId) return;
      
      if (pricing) {
        const { error } = await supabase
          .from("sns_package_pricing")
          .update(newPricing)
          .eq("id", pricing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("sns_package_pricing")
          .insert([{ ...newPricing, package_id: selectedPackageId }]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["package-pricing"] });
      toast({ title: "Success", description: "Pricing updated successfully" });
    }
  });

  const addAddon = useMutation({
    mutationFn: async () => {
      if (!selectedPackageId) return;
      const { error } = await supabase
        .from("sns_package_addons")
        .insert([{ ...newAddon, package_id: selectedPackageId }]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["package-addons"] });
      setNewAddon({
        name: "",
        description: "",
        price: 0,
        addon_type: "activity",
        is_per_person: false,
        is_per_night: false,
        sort_order: 0
      });
      toast({ title: "Success", description: "Addon added successfully" });
    }
  });

  const deleteAddon = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("sns_package_addons")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["package-addons"] });
    }
  });

  const addRoom = useMutation({
    mutationFn: async () => {
      if (!selectedPackageId) return;
      const { error } = await supabase
        .from("sns_room_packages")
        .insert([{ ...newRoom, package_id: selectedPackageId }]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["room-packages"] });
      setNewRoom({
        room_type: "",
        room_name: "",
        capacity: 2,
        base_price: 0,
        price_per_extra_person: 0,
        max_extra_persons: 0,
        amenities: []
      });
      toast({ title: "Success", description: "Room added successfully" });
    }
  });

  const updateRoom = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const { error } = await supabase
        .from("sns_room_packages")
        .update(data)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["room-packages"] });
      setEditingRoom(null);
      setNewRoom({
        room_type: "",
        room_name: "",
        capacity: 2,
        base_price: 0,
        price_per_extra_person: 0,
        max_extra_persons: 0,
        amenities: []
      });
      toast({ title: "Success", description: "Room updated successfully" });
    }
  });

  const deleteRoom = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("sns_room_packages")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["room-packages"] });
    }
  });

  // Update pricing state when data loads
  useEffect(() => {
    if (pricing) {
      setNewPricing({
        base_price: pricing.base_price,
        per_person_price: pricing.per_person_price,
        per_night_price: pricing.per_night_price,
        min_nights: pricing.min_nights,
        max_nights: pricing.max_nights,
        min_persons: pricing.min_persons,
        max_persons: pricing.max_persons
      });
    } else {
      // Reset to defaults when no pricing data
      setNewPricing({
        base_price: 0,
        per_person_price: 0,
        per_night_price: 0,
        min_nights: 1,
        max_nights: 30,
        min_persons: 1,
        max_persons: 10
      });
    }
  }, [pricing]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Enhanced Package Management</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Select Package</label>
            <Select 
              value={selectedPackageId?.toString() || ""} 
              onValueChange={(value) => setSelectedPackageId(parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choose a package to manage" />
              </SelectTrigger>
              <SelectContent>
                {packages.map((pkg) => (
                  <SelectItem key={pkg.id} value={pkg.id.toString()}>
                    {pkg.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedPackageId && (
            <Tabs defaultValue="pricing" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="pricing">Pricing</TabsTrigger>
                <TabsTrigger value="addons">Activities & Services</TabsTrigger>
                <TabsTrigger value="rooms">Room Packages</TabsTrigger>
              </TabsList>

              <TabsContent value="pricing" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Pricing Configuration</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Base Price ($)</label>
                        <Input
                          type="number"
                          value={newPricing.base_price}
                          onChange={(e) => setNewPricing(prev => ({ ...prev, base_price: parseFloat(e.target.value) || 0 }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Per Person Price ($)</label>
                        <Input
                          type="number"
                          value={newPricing.per_person_price}
                          onChange={(e) => setNewPricing(prev => ({ ...prev, per_person_price: parseFloat(e.target.value) || 0 }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Per Night Price ($)</label>
                        <Input
                          type="number"
                          value={newPricing.per_night_price}
                          onChange={(e) => setNewPricing(prev => ({ ...prev, per_night_price: parseFloat(e.target.value) || 0 }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Min Nights</label>
                        <Input
                          type="number"
                          value={newPricing.min_nights}
                          onChange={(e) => setNewPricing(prev => ({ ...prev, min_nights: parseInt(e.target.value) || 1 }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Max Nights</label>
                        <Input
                          type="number"
                          value={newPricing.max_nights}
                          onChange={(e) => setNewPricing(prev => ({ ...prev, max_nights: parseInt(e.target.value) || 30 }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Min Persons</label>
                        <Input
                          type="number"
                          value={newPricing.min_persons}
                          onChange={(e) => setNewPricing(prev => ({ ...prev, min_persons: parseInt(e.target.value) || 1 }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Max Persons</label>
                        <Input
                          type="number"
                          value={newPricing.max_persons}
                          onChange={(e) => setNewPricing(prev => ({ ...prev, max_persons: parseInt(e.target.value) || 10 }))}
                        />
                      </div>
                    </div>
                    <Button onClick={() => savePricing.mutate()}>
                      Save Pricing
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="addons" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Add New Activity/Service</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Name</label>
                        <Input
                          value={newAddon.name}
                          onChange={(e) => setNewAddon(prev => ({ ...prev, name: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Price ($)</label>
                        <Input
                          type="number"
                          value={newAddon.price}
                          onChange={(e) => setNewAddon(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Type</label>
                        <Select
                          value={newAddon.addon_type}
                          onValueChange={(value) => setNewAddon(prev => ({ ...prev, addon_type: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="activity">Activity</SelectItem>
                            <SelectItem value="service">Service</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Sort Order</label>
                        <Input
                          type="number"
                          value={newAddon.sort_order}
                          onChange={(e) => setNewAddon(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Description</label>
                      <Textarea
                        value={newAddon.description}
                        onChange={(e) => setNewAddon(prev => ({ ...prev, description: e.target.value }))}
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={newAddon.is_per_person}
                        onCheckedChange={(checked) => setNewAddon(prev => ({ ...prev, is_per_person: checked }))}
                      />
                      <label className="text-sm font-medium">Per Person</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={newAddon.is_per_night}
                        onCheckedChange={(checked) => setNewAddon(prev => ({ ...prev, is_per_night: checked }))}
                      />
                      <label className="text-sm font-medium">Per Night</label>
                    </div>
                    <Button onClick={() => addAddon.mutate()}>Add Addon</Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Manage Activities/Services</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {addons.map((addon) => (
                        <div key={addon.id} className="border rounded-lg p-4 flex items-center justify-between">
                          <div>
                            <h3 className="font-medium">{addon.name}</h3>
                            <p className="text-sm text-gray-500">{addon.description}</p>
                            <p className="text-sm text-shell-primary font-medium">
                              ${addon.price}
                              {addon.is_per_person && " per person"}
                              {addon.is_per_night && " per night"}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteAddon.mutate(addon.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="rooms" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Add New Room Package</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Room Type</label>
                        <Input
                          value={newRoom.room_type}
                          onChange={(e) => setNewRoom(prev => ({ ...prev, room_type: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Room Name</label>
                        <Input
                          value={newRoom.room_name}
                          onChange={(e) => setNewRoom(prev => ({ ...prev, room_name: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Capacity</label>
                        <Input
                          type="number"
                          value={newRoom.capacity}
                          onChange={(e) => setNewRoom(prev => ({ ...prev, capacity: parseInt(e.target.value) || 2 }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Base Price ($)</label>
                        <Input
                          type="number"
                          value={newRoom.base_price}
                          onChange={(e) => setNewRoom(prev => ({ ...prev, base_price: parseFloat(e.target.value) || 0 }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Price per Extra Person ($)</label>
                        <Input
                          type="number"
                          value={newRoom.price_per_extra_person}
                          onChange={(e) => setNewRoom(prev => ({ ...prev, price_per_extra_person: parseFloat(e.target.value) || 0 }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Max Extra Persons</label>
                        <Input
                          type="number"
                          value={newRoom.max_extra_persons}
                          onChange={(e) => setNewRoom(prev => ({ ...prev, max_extra_persons: parseInt(e.target.value) || 0 }))}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Amenities (comma separated)</label>
                      <Input
                        value={newRoom.amenities.join(',')}
                        onChange={(e) => setNewRoom(prev => ({ ...prev, amenities: e.target.value.split(',').map(s => s.trim()) }))}
                      />
                    </div>
                     <div className="flex gap-2">
                       <Button onClick={() => editingRoom ? updateRoom.mutate({ id: editingRoom.id, data: newRoom }) : addRoom.mutate()}>
                         {editingRoom ? "Update Room" : "Add Room"}
                       </Button>
                       {editingRoom && (
                         <Button variant="outline" onClick={() => {
                           setEditingRoom(null);
                           setNewRoom({
                             room_type: "",
                             room_name: "",
                             capacity: 2,
                             base_price: 0,
                             price_per_extra_person: 0,
                             max_extra_persons: 0,
                             amenities: []
                           });
                         }}>
                           Cancel
                         </Button>
                       )}
                     </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Manage Room Packages</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {rooms.map((room) => (
                         <div key={room.id} className="border rounded-lg p-4 flex items-center justify-between">
                           <div>
                             <h3 className="font-medium">{room.room_name}</h3>
                             <p className="text-sm text-gray-500">{room.room_type}</p>
                             <p className="text-sm text-gray-500">Capacity: {room.capacity}</p>
                             <p className="text-sm text-shell-primary font-medium">Base Price: ${room.base_price}</p>
                           </div>
                           <div className="flex gap-2">
                             <Button
                               variant="outline"
                               size="icon"
                               onClick={() => {
                                 setEditingRoom(room);
                                 setNewRoom({
                                   room_type: room.room_type,
                                   room_name: room.room_name,
                                   capacity: room.capacity,
                                   base_price: room.base_price,
                                   price_per_extra_person: room.price_per_extra_person,
                                   max_extra_persons: room.max_extra_persons,
                                   amenities: room.amenities || []
                                 });
                               }}
                             >
                               <Edit className="w-4 h-4" />
                             </Button>
                             <Button
                               variant="ghost"
                               size="icon"
                               onClick={() => deleteRoom.mutate(room.id)}
                             >
                               <Trash2 className="w-4 h-4 text-red-500" />
                             </Button>
                           </div>
                         </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Default export for EnhancedPackageManager component
export default EnhancedPackageManager;
